import { DatePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, Inject, inject } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatTableModule } from '@angular/material/table'
import { MatTooltipModule } from '@angular/material/tooltip'
import { DEV_OR_LOCALHOST } from '@bases/base.token'

import { DialogWarnComponent } from '@components/dialogs/dialog-warn/dialog-warn.component'
import { Snapshot } from '@core/models/snapshot.model'
import { SnapshotService } from '@core/services/snapshot.service'
import { FormatSharedWithPipe } from '@shared/pipes/format-shared-with.pipe'
import { BehaviorSubject } from 'rxjs'
import { DialogSnapshotComponent } from '../dialog-snapshot/dialog-snapshot.component'

@Component({
  selector: 'app-dialog-snapshots-list',
  templateUrl: './dialog-snapshots-list.component.html',
  styleUrl: './dialog-snapshots-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    // Pipe
    DatePipe,

    // Forms
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,

    // Material
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
})
export class DialogSnapshotsListComponent {
  readonly dialogRef = inject(MatDialogRef<DialogSnapshotsListComponent>)

  snapshotsState$ = this._snapshotService.snapshotsState$
  snapshotsSharedWithMeState$ = this._snapshotService.snapshotsSharedWithMeState$

  displayedColumns: string[] = ['name', 'description', 'shared', 'date', 'actions']

  constructor(
    @Inject(DEV_OR_LOCALHOST) public devOrLocalhost: BehaviorSubject<string>,
    private _dialog: MatDialog,
    private _snapshotService: SnapshotService,
  ) {}

  ngOnInit() {
    this._snapshotService.refreshSnapshots()
  }

  refreshSharedSnapshots() {
    this._snapshotService.getSnapshotsSharedWithMe()
  }

  cancel(): void {
    this.dialogRef.close()
  }

  async applySnapshot(snapshot: Snapshot) {
    const result = await this._snapshotService.apply(snapshot)
    if (result) this.dialogRef.close()
  }

  editSnapshot(snapshot: Snapshot) {
    const tempEmailList = snapshot.sharedWith?.map((item: string) => item).join('; ')

    const snapshotToEdit = {
      id: snapshot.id,
      name: snapshot.name,
      description: snapshot.description,
      shared_with: tempEmailList,
    }

    const dialogRef = this._dialog.open(DialogSnapshotComponent, {
      width: '50vw',
      data: snapshotToEdit,
    })

    dialogRef.afterClosed().subscribe((update) => {
      if (update) {
        this._snapshotService.getSnapshots()
      }
    })
  }

  async deleteSnapshotWithConfirm(snapshot: Snapshot) {
    const title = 'Delete snapshot'
    const question = `Are you sure you want to delete the snapshot ${snapshot.name} ?`
    const data = {
      title: title,
      message: question,
    }
    const dialogRef = this._dialog.open(DialogWarnComponent, {
      data: data,
    })

    dialogRef.afterClosed().subscribe(async (deletionConfirm) => {
      if (deletionConfirm && snapshot.id) {
        const response = await this._snapshotService.deleteSnapshot(snapshot.id)
        if (response === null) return
        this._snapshotService.refreshSnapshots()
      }
    })
  }

  async deleteSharedSnapshotWithConfirm(snapshot: Snapshot) {
    const title = 'Remove shared snapshot'
    const question = `Are you sure you want to remove the snapshot "${snapshot.name}" that has been shared to you ?`
    const data = {
      title: title,
      message: question,
    }
    const dialogRef = this._dialog.open(DialogWarnComponent, {
      data: data,
    })

    dialogRef.afterClosed().subscribe(async (deletionConfirm) => {
      if (deletionConfirm && snapshot.id) {
        const response = await this._snapshotService.deleteSharedSnapshot(snapshot.id)
        if (response === null) return
        this._snapshotService.refreshSnapshots()
      }
    })
  }

  formatTooltip(sharedWith: string[]) {
    const pipe = new FormatSharedWithPipe()
    return 'Shared with ' + pipe.transform(sharedWith)
  }
}
